import { useEffect, useRef, useState, useCallback } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from '../../i18n'
import { trainingApi } from '../../api'
import type { ThisWeekTraining } from '../../types/api'
import { useTheme } from '../../hooks/useTheme'
import { getTrainingDisplayStatus, translateTrainingStatus } from '../../utils/statusTranslations'
import { formatLongDate } from '../../utils/dateTranslations'
import {
  findOverlappingGroups,
  formatLocalDateTimeForAPI,
  formatTimeRange,
  formatTraineeDisplayName,
} from '../../utils/trainingUtils'
import { useAuth } from '../../contexts/AuthContext'
import { Avatar, ButtonGroup, Badge, Card, ErrorMessage, StatusBadge } from '../ui'

type ViewMode = 'week' | 'day'

function getStatusVariant(status: string): 'success' | 'warning' | 'info' | 'error' | 'default' | 'brand' {
  switch (status) {
    case 'COMPLETED': return 'success'
    case 'ACTIVE': return 'brand'
    case 'UPCOMING': return 'info'
    case 'MOVED': return 'warning'
    case 'SKIPPED':
    case 'CANCELLED':
    case 'FIRED': return 'error'
    default: return 'default'
  }
}

export default function TrainingsView() {
  const navigate = useNavigate()
  const location = useLocation()
  const theme = useTheme()
  const { t } = useTranslation()
  const { user } = useAuth()
  const isCoach = user?.hasTrainees ?? false

  const initialViewMode = (location.state as { viewMode?: ViewMode })?.viewMode || 'week'
  const [trainings, setTrainings] = useState<ThisWeekTraining[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [viewMode, setViewMode] = useState<ViewMode>(initialViewMode)
  const [showPastTrainings, setShowPastTrainings] = useState(false)
  const nextTrainingRef = useRef<HTMLDivElement>(null)

  const loadData = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const now = new Date()
      let from: string
      let to: string

      if (viewMode === 'day') {
        const today = new Date(now)
        today.setHours(0, 0, 0, 0)
        const endOfDay = new Date(today)
        endOfDay.setHours(23, 59, 59, 999)
        from = formatLocalDateTimeForAPI(today)
        to = formatLocalDateTimeForAPI(endOfDay)
      } else {
        const dayOfWeek = now.getDay()
        const daysToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek
        const monday = new Date(now)
        monday.setDate(now.getDate() + daysToMonday)
        monday.setHours(0, 0, 0, 0)
        const sundayEnd = new Date(monday)
        sundayEnd.setDate(monday.getDate() + 6)
        sundayEnd.setHours(23, 59, 59, 999)
        from = formatLocalDateTimeForAPI(monday)
        to = formatLocalDateTimeForAPI(sundayEnd)
      }

      const trainingsList = await trainingApi.getTrainingsInRange(from, to)
      setTrainings(trainingsList)
    } catch (err) {
      console.error('Error loading trainings:', err)
      setError(err instanceof Error ? err.message : 'Failed to load trainings')
    } finally {
      setLoading(false)
    }
  }, [viewMode])

  useEffect(() => {
    loadData()
  }, [loadData])

  useEffect(() => {
    if (!loading && trainings.length > 0 && viewMode === 'week' && nextTrainingRef.current) {
      setTimeout(() => {
        nextTrainingRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }, 100)
    }
  }, [loading, trainings, viewMode])

  const findNextTraining = (list: ThisWeekTraining[]): number | null => {
    const now = new Date()
    const upcoming = list.filter(tr => new Date(tr.start) > now)
    if (upcoming.length === 0) return null
    return upcoming.reduce((a, b) => new Date(a.start) < new Date(b.start) ? a : b).projectionId
  }

  const groupTrainingsByDay = (list: ThisWeekTraining[]) => {
    const groups: Record<string, ThisWeekTraining[]> = {}
    list.forEach(tr => {
      const key = new Date(tr.start).toISOString().split('T')[0]
      groups[key] = [...(groups[key] ?? []), tr]
    })
    return Object.keys(groups).sort().map(dayKey => ({ dayKey, trainings: groups[dayKey] }))
  }

  const sectionLabel: React.CSSProperties = {
    fontSize: '11px',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    color: theme.text.muted,
    opacity: 0.7,
  }

  const renderTrainingCard = (training: ThisWeekTraining, isNextTraining: boolean, isPast = false) => {
    const displayStatus = getTrainingDisplayStatus(training.status, training.start, training.end)
    const statusKey = displayStatus[0]
    const showStatusBadge = statusKey !== 'ACTIVE' && statusKey !== 'UPCOMING'
    const traineeId = training.isCoach ? training.otherUserId : user?.id

    return (
      <Card
        key={training.projectionId}
        ref={isNextTraining && !isPast ? nextTrainingRef : undefined}
        onClick={training.isCoach ? () => {
          navigate(`/trainee/${traineeId}/set/${training.setId}/training/${training.projectionId}`, {
            state: { fromPath: '/this-week-trainings' }
          })
        } : undefined}
        variant={training.isCoach ? 'brand' : 'default'}
        showArrow={training.isCoach}
      >
        <div className={`flex items-center gap-3 ${training.isCoach ? 'pr-6' : ''}`}>
          <Avatar
            photoUrl={training.otherUserPhotoUrl ?? undefined}
            name={formatTraineeDisplayName(training)}
            size="md"
            fallbackBg={theme.accent.light}
            className="flex-shrink-0"
          />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-semibold" style={{ color: theme.text.primary }}>
                {formatTraineeDisplayName(training)}
              </span>
              {isNextTraining && !isPast && (
                <Badge variant="success" size="sm" className="rounded-full">
                  {t.thisWeekTrainings.nextTraining}
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
              <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: theme.text.muted, flexShrink: 0 }}>
                <circle cx="12" cy="12" r="10"/>
                <polyline points="12 6 12 12 16 14"/>
              </svg>
              <span className="text-xs" style={{ color: theme.text.muted }}>
                {formatTimeRange(training.start, training.end)}
              </span>
              <span
                className="text-xs px-1.5 py-0.5 rounded-full font-medium"
                style={{
                  backgroundColor: `${theme.buttonColor}12`,
                  border: `1px solid ${theme.buttonColor}25`,
                  color: theme.accent.text,
                }}
              >
                {training.isCoach ? t.thisWeekTrainings.asCoach : t.thisWeekTrainings.asTrainee}
              </span>
            </div>
          </div>
          {showStatusBadge && (
            <StatusBadge
              status={translateTrainingStatus(statusKey, t)}
              variant={getStatusVariant(statusKey)}
              size="sm"
            />
          )}
        </div>
      </Card>
    )
  }

  const renderDayGroup = (dayKey: string, dayTrainings: ThisWeekTraining[], nextTrainingId: number | null, isPast = false) => (
    <div key={dayKey}>
      <div style={sectionLabel} className="mb-3 px-1">
        {formatLongDate(new Date(dayTrainings[0].start), t)}
      </div>
      <div className="flex flex-col gap-3">
        {findOverlappingGroups(dayTrainings).map((group, groupIdx) => (
          <div key={`group-${groupIdx}`}>
            {group.length > 1 && (
              <Badge variant="accent" size="sm" className="mb-2">
                {t.thisWeekTrainings.sameTime} ({group.length})
              </Badge>
            )}
            {group.length > 1 ? (
              <Card variant="overlap" className="gap-2">
                {group.map(tr => renderTrainingCard(tr, tr.projectionId === nextTrainingId, isPast))}
              </Card>
            ) : (
              <div className="flex flex-col gap-3">
                {group.map(tr => renderTrainingCard(tr, tr.projectionId === nextTrainingId, isPast))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  )

  return (
    <div className="p-5">
      <header className="mb-6" data-tour="tour-page-schedule">
        <h1 className="text-2xl font-bold mb-1" style={{ color: theme.text.primary }}>
          {viewMode === 'week' ? t.thisWeekTrainings.title : t.thisWeekTrainings.todayTitle}
        </h1>
        <p className="text-sm" style={{ color: theme.text.muted }}>
          {viewMode === 'week' ? t.thisWeekTrainings.subtitle : t.thisWeekTrainings.todaySubtitle}
        </p>
      </header>

      <div className="mb-6">
        <ButtonGroup
          options={[
            { value: 'week', label: t.thisWeekTrainings.weekView },
            { value: 'day', label: t.thisWeekTrainings.dayView },
          ]}
          value={viewMode}
          onChange={(val) => setViewMode(val as ViewMode)}
          variant="primary"
          fullWidth
        />
      </div>

      {viewMode === 'day' && isCoach && (
        <div
          className="mb-5 rounded-xl p-4 flex items-center gap-3 cursor-pointer active:opacity-75 transition-opacity"
          style={{ backgroundColor: `${theme.buttonColor}10`, border: `1px solid ${theme.buttonColor}25` }}
          onClick={() => navigate('/pro-view')}
        >
          <div
            className="w-9 h-9 rounded-xl flex items-center justify-center text-lg shrink-0"
            style={{ backgroundColor: `${theme.buttonColor}20` }}
          >
            ⚡
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold" style={{ color: theme.text.primary }}>{t.proView.title}</p>
            <p className="text-xs" style={{ color: theme.text.muted }}>{t.proView.bannerHint}</p>
          </div>
          <span className="text-xl opacity-50 shrink-0">→</span>
        </div>
      )}

      {loading ? (
        <div className="text-center py-10 text-base" style={{ color: theme.text.muted }}>{t.common.loading}</div>
      ) : error ? (
        <ErrorMessage title="Error" message={error} icon="⚠️" onRetry={loadData} />
      ) : trainings.length > 0 ? (
        <div className="flex flex-col gap-6">
          {(() => {
            const nextTrainingId = findNextTraining(trainings)
            const now = new Date()
            const pastTrainings = viewMode === 'week' ? trainings.filter(tr => new Date(tr.end) < now) : []
            const futureTrainings = viewMode === 'week' ? trainings.filter(tr => new Date(tr.end) >= now) : trainings
            const pastByDay = groupTrainingsByDay(pastTrainings)
            const futureByDay = groupTrainingsByDay(futureTrainings)

            return (
              <>
                {viewMode === 'week' && pastByDay.length > 0 && (
                  <div className="flex flex-col gap-3">
                    <button
                      type="button"
                      onClick={() => setShowPastTrainings(!showPastTrainings)}
                      className="flex items-center justify-between px-4 py-3 rounded-xl transition-opacity hover:opacity-80 active:opacity-60"
                      style={{ backgroundColor: theme.cardBg, border: `1px solid ${theme.cardBorder}` }}
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium" style={{ color: theme.text.muted }}>
                          {t.trainingSetDetail.schedule.pastTrainings}
                        </span>
                        <span
                          className="text-xs px-1.5 py-0.5 rounded-full font-medium"
                          style={{ backgroundColor: `${theme.text.muted}18`, color: theme.text.muted }}
                        >
                          {pastTrainings.length}
                        </span>
                      </div>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        style={{
                          color: theme.text.muted,
                          transform: showPastTrainings ? 'rotate(90deg)' : 'rotate(0deg)',
                          transition: 'transform 0.2s ease',
                          flexShrink: 0,
                        }}
                      >
                        <polyline points="9 18 15 12 9 6"/>
                      </svg>
                    </button>

                    {showPastTrainings && (
                      <div className="flex flex-col gap-6" style={{ opacity: 0.65 }}>
                        {pastByDay.map(({ dayKey, trainings: dt }) =>
                          renderDayGroup(dayKey, dt, nextTrainingId, true)
                        )}
                      </div>
                    )}
                  </div>
                )}

                <div className="flex flex-col gap-6">
                  {futureByDay.map(({ dayKey, trainings: dt }) =>
                    renderDayGroup(dayKey, dt, nextTrainingId)
                  )}
                </div>
              </>
            )
          })()}
        </div>
      ) : (
        <div className="flex flex-col items-center py-14 text-center">
          <span className="text-5xl mb-4">💪</span>
          <p className="font-semibold mb-1" style={{ color: theme.text.primary }}>
            {viewMode === 'week' ? t.thisWeekTrainings.emptyTitle : t.thisWeekTrainings.emptyTodayTitle}
          </p>
          <p className="text-sm px-8" style={{ color: theme.text.muted }}>
            {viewMode === 'week' ? t.thisWeekTrainings.emptyMessage : t.thisWeekTrainings.emptyTodayMessage}
          </p>
        </div>
      )}
    </div>
  )
}
